import type {
  ApiSuccessResponse,
  PaginatedResult,
  PaginationMeta,
} from "@/types/common";

/** Unwrap `{ success, data }` envelopes from the API. */
export function unwrapData<T>(response: unknown): T {
  if (
    response !== null &&
    typeof response === "object" &&
    "data" in response &&
    (response as ApiSuccessResponse<unknown>).data !== undefined
  ) {
    return (response as ApiSuccessResponse<T>).data;
  }
  return response as T;
}

/** Normalize list responses that may be a bare array or paginated `{ items, meta }`. */
export function unwrapList<T>(response: unknown): T[] {
  const data = unwrapData<unknown>(response);
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object" && "items" in data) {
    return (data as PaginatedResult<T>).items;
  }
  return [];
}

function toPaginationMeta(
  source: Record<string, unknown>,
  itemsLength: number,
): PaginationMeta {
  return {
    total: Number(source.total) || itemsLength,
    page: Number(source.page) || 1,
    limit: Number(source.limit) || itemsLength || 20,
    totalPages: Number(source.totalPages) || 1,
  };
}

export function unwrapPaginated<T>(response: unknown): PaginatedResult<T> {
  const data = unwrapData<unknown>(response);

  if (data && typeof data === "object" && "items" in data) {
    const paginated = data as Record<string, unknown>;
    const items = Array.isArray(paginated.items)
      ? (paginated.items as T[])
      : [];

    if (
      paginated.meta &&
      typeof paginated.meta === "object" &&
      !Array.isArray(paginated.meta)
    ) {
      return {
        items,
        meta: paginated.meta as PaginationMeta,
      };
    }

    // Strapi's shared `paginated()` helper (mof-strapi/src/utils/governance.ts)
    // returns `{ items, pagination: { page, pageSize, total, pageCount } }`.
    if (
      paginated.pagination &&
      typeof paginated.pagination === "object" &&
      !Array.isArray(paginated.pagination)
    ) {
      const p = paginated.pagination as Record<string, unknown>;
      return {
        items,
        meta: {
          total: Number(p.total) || items.length,
          page: Number(p.page) || 1,
          limit: Number(p.pageSize) || items.length || 20,
          totalPages: Number(p.pageCount) || 1,
        },
      };
    }

    if ("total" in paginated) {
      return {
        items,
        meta: toPaginationMeta(paginated, items.length),
      };
    }

    return {
      items,
      meta: toPaginationMeta({}, items.length),
    };
  }

  const items = Array.isArray(data) ? (data as T[]) : [];
  return {
    items,
    meta: toPaginationMeta({}, items.length),
  };
}
